import { Route, Routes, Navigate, useSearchParams } from "react-router-dom";
import * as RadixTooltip from "@radix-ui/react-tooltip";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { AppShell } from "./components/AppShell";
import SignIn from "./routes/SignIn";
import AccessDenied from "./routes/AccessDenied";
import Home from "./routes/Home";
import Entities from "./routes/Entities";
import EntityDetail from "./routes/EntityDetail";
import Renewals from "./routes/Renewals";
import RegulatoryWatch from "./routes/RegulatoryWatch";
import History from "./routes/History";
import Chat from "./routes/Chat";
import Admin from "./routes/Admin";
import AdminDeletedEntities from "./routes/AdminDeletedEntities";
import AdminDeletedEntityView from "./routes/AdminDeletedEntityView";
import Settings from "./routes/Settings";
import NotFound from "./routes/NotFound";

/**
 * Preserves deep-link query params when redirecting from the old /audit-log
 * path to the new /history path (e.g. ?entityId=…&field=… bookmarks still work).
 */
function AuditLogRedirect(): JSX.Element {
  const [searchParams] = useSearchParams();
  const qs = searchParams.toString();
  const to = qs ? `/history?${qs}` : "/history";
  return <Navigate to={to} replace />;
}

export default function App(): JSX.Element {
  return (
    <ErrorBoundary>
      <RadixTooltip.Provider delayDuration={300}>
      <Routes>
        <Route path="/auth/signin" element={<SignIn />} />
        {/* Public — shown when invitation gate rejects a sign-in OR when an admin-revoked user attempts to sign in */}
        <Route path="/access-denied" element={<AccessDenied />} />

        <Route
          element={
            <ProtectedRoute>
              <AppShell />
            </ProtectedRoute>
          }
        >
          <Route index element={<Home />} />
          <Route path="entities" element={<Entities />} />
          <Route path="entities/:id" element={<EntityDetail />} />
          <Route path="renewals" element={<Renewals />} />
          <Route path="regulatory-watch" element={<RegulatoryWatch />} />
          {/* History — visible to all authenticated users */}
          <Route path="history" element={<History />} />
          {/* Redirect: /audit-log → /history (preserves query params for deep-links) */}
          <Route path="audit-log" element={<AuditLogRedirect />} />
          <Route path="chat" element={<Chat />} />
          <Route
            path="admin"
            element={
              <ProtectedRoute requireRole="admin">
                <Admin />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/deleted-entities"
            element={
              <ProtectedRoute requireRole="admin">
                <AdminDeletedEntities />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/deleted-entities/:id"
            element={
              <ProtectedRoute requireRole="admin">
                <AdminDeletedEntityView />
              </ProtectedRoute>
            }
          />
          <Route path="settings" element={<Settings />} />
        </Route>

        <Route path="/auth/*" element={<Navigate to="/auth/signin" replace />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      </RadixTooltip.Provider>
    </ErrorBoundary>
  );
}
